// Everything a document says in markup, written from page-meta.ts's words and a utility's content: the head the build
// emits, the body a reader with no script is handed, and the article the application draws under a utility. It reaches
// for nothing but page-meta.ts and assumes no DOM, for the reason that module gives: scripts/build.ts loads it too.
import { ATTRIBUTIONS_PATH, canonicalUrl, CATEGORIES, documentFileName, escapeHtml, headMeta, HOME_PATH, isPagePath, isUtilityPath, pageMeta, type PagePath, pagePaths, SITE_NAME, SITE_ORIGIN, type UtilityPath, utilityPaths } from "./page-meta.ts";

// What a utility's page says below the tool, in the order it says it. Each is a module of src/page-content/ read twice:
// by the build, into the document it emits for that address, and by the application, under the tool it draws.
export interface PageContent {
  // Three to five utilities somebody on this page plausibly wants next, in the order they would want them.
  related: UtilityPath[];
  howItWorks: Block[];
  examples: Titled[];
  problems: Titled[];
  faq: Question[];
  references: Reference[];
}

// A paragraph, a code block, a list or a table whose first row is its head. Text anywhere takes `code` and
// [a link](/path) and nothing else, which is the whole of what the renderer below knows about inline markup.
export type Block = string | { code: string } | { list: string[] } | { table: string[][] };

export interface Titled {
  title: string;
  blocks: Block[];
}

// One short paragraph, since the same words go into the FAQPage graph and a paragraph is all an answer there can be.
export interface Question {
  question: string;
  answer: string;
}

export interface Reference {
  title: string;
  url: string;
}

export type PageContents = Partial<Record<UtilityPath, PageContent>>;

export const SECTION_TITLES = {
  howItWorks: "How it works",
  examples: "Examples",
  problems: "Common problems",
  faq: "Frequently asked questions",
  related: "Related tools",
  references: "References",
} as const;

// The article under a utility, lede first: the build puts it under the page's headline and the application under the
// tool, and both write the same string, so a crawler reading either reads the same page.
export function articleHtml(path: UtilityPath, content: PageContent): string {
  return [
    `<p class="page-lede">${escapeHtml(pageMeta(path).description)}</p>`,
    section(SECTION_TITLES.howItWorks, content.howItWorks.map(blockHtml)),
    section(SECTION_TITLES.examples, content.examples.map(titledHtml)),
    section(SECTION_TITLES.problems, content.problems.map(titledHtml)),
    section(SECTION_TITLES.faq, content.faq.map(questionHtml)),
    section(SECTION_TITLES.related, [listHtml(content.related.map(relatedHtml))]),
    section(SECTION_TITLES.references, [listHtml(content.references.map(referenceHtml))]),
  ].join("\n");
}

function section(title: string, parts: string[]): string {
  return ["<section>", `<h2>${escapeHtml(title)}</h2>`, ...parts, "</section>"].join("\n");
}

function titledHtml({ title, blocks }: Titled): string {
  return [`<h3>${inlineHtml(title)}</h3>`, ...blocks.map(blockHtml)].join("\n");
}

function questionHtml({ question, answer }: Question): string {
  return `<h3>${inlineHtml(question)}</h3>\n<p>${inlineHtml(answer)}</p>`;
}

// The page's own headline is the anchor, being the words the page is found by rather than the navbar's one word.
function relatedHtml(path: UtilityPath): string {
  return `<a href="${path}">${escapeHtml(pageMeta(path).title)}</a>`;
}

function referenceHtml({ title, url }: Reference): string {
  return linkHtml(url, escapeHtml(title));
}

function blockHtml(block: Block): string {
  if (typeof block === "string") return `<p>${inlineHtml(block)}</p>`;
  if ("code" in block) return `<pre><code>${escapeHtml(block.code)}</code></pre>`;
  if ("list" in block) return listHtml(block.list.map(inlineHtml));

  const [head, ...rows] = block.table;
  const cells = (row: string[], tag: string) => row.map((cell) => `<${tag}>${inlineHtml(cell)}</${tag}>`).join("");
  // Wrapped so a table wider than a phone scrolls inside itself rather than taking the page sideways with it.
  return [
    "<div class=\"page-table\"><table>",
    `<thead><tr>${cells(head, "th")}</tr></thead>`,
    `<tbody>${rows.map((row) => `<tr>${cells(row, "td")}</tr>`).join("")}</tbody>`,
    "</table></div>",
  ].join("\n");
}

// One item per line, so a count of the links in a document is a count of lines that hold one.
function listHtml(items: string[]): string {
  return ["<ul>", ...items.map((item) => `<li>${item}</li>`), "</ul>"].join("\n");
}

const INLINE = /`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;

export function inlineHtml(text: string): string {
  let html = "";
  let from = 0;
  for (const match of text.matchAll(INLINE)) {
    html += escapeHtml(text.slice(from, match.index));
    html += match[1] !== undefined ? `<code>${escapeHtml(match[1])}</code>` : linkHtml(match[3], escapeHtml(match[2]));
    from = match.index + match[0].length;
  }
  return html + escapeHtml(text.slice(from));
}

// The text a reader is shown once the markup is drawn, which is what a graph that restates the page has to say.
export function plainText(text: string): string {
  return text.replace(INLINE, (_, code: string | undefined, label: string) => code ?? label);
}

// A path is a page of this site and routes in place; anything else opens beside the page rather than taking its state.
function linkHtml(href: string, html: string): string {
  return href.startsWith("/")
    ? `<a href="${escapeHtml(href)}">${html}</a>`
    : `<a href="${escapeHtml(href)}" rel="noopener noreferrer" target="_blank">${html}</a>`;
}

export interface Crumb {
  label: string;
  path: PagePath;
}

// The welcome page and the page itself, which is the whole depth of a site with no page under another.
export function crumbs(path: PagePath): Crumb[] {
  return path === HOME_PATH ? [] : [{ label: SITE_NAME, path: HOME_PATH }, { label: pageMeta(path).label, path }];
}

// What the graph in a document's head says about it, off the same words the document is written from. The welcome page
// says what the site is, who publishes it and which utilities it holds; a utility says what it is, where it sits, and
// the questions its page answers; attributions only where it sits. An address with no page says nothing at all.
export function structuredData(path: string, content?: PageContent): StructuredData | undefined {
  if (path === HOME_PATH) return graph([websiteData(), ORGANIZATION, itemListData()]);
  if (isUtilityPath(path)) {
    const nodes = [utilityData(path), breadcrumbData(path)];
    if (content && content.faq.length > 0) nodes.push(faqData(path, content.faq));
    return graph(nodes);
  }
  if (path === ATTRIBUTIONS_PATH) return graph([breadcrumbData(path)]);
  return undefined;
}

function graph(nodes: Record<string, unknown>[]): StructuredData {
  return { "@context": "https://schema.org", "@graph": nodes };
}

// A fragment rather than the address itself, so a reader can tell the site apart from the page that describes it.
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;

// Who the footer says the site is by.
const ORGANIZATION = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "Manualpilot",
  url: "https://manualpilot.com",
  sameAs: ["https://github.com/manualpilot"],
};

function websiteData(): Record<string, unknown> {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    url: canonicalUrl(HOME_PATH),
    description: pageMeta(HOME_PATH).description,
    inLanguage: "en",
    publisher: { "@id": ORGANIZATION_ID },
  };
}

// The same list the welcome page renders as links, which is what keeps this a reading of the page and not a claim.
function itemListData(): Record<string, unknown> {
  return {
    "@type": "ItemList",
    name: `${SITE_NAME} utilities`,
    numberOfItems: utilityPaths().length,
    itemListElement: utilityPaths().map((utility, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: utilityData(utility),
    })),
  };
}

// The url is what ties an entry to the document that page's own head was written into, and everything else here is
// true of every one of them: the whole site is one free browser application and none of it runs anywhere else.
// A WebApplication is a SoftwareApplication, so Google's rich results test reads every entry as a Software App
// and warns that each is missing an aggregateRating: that feature is gated on ratings this site has no honest
// source for, and inventing them is what the guidelines forbid, so the warning is the answer and not a fault.
function utilityData(path: UtilityPath): Record<string, unknown> {
  const meta = pageMeta(path);

  return {
    "@type": "WebApplication",
    name: meta.title,
    description: meta.description,
    url: canonicalUrl(path),
    applicationCategory: "DeveloperApplication",
    operatingSystem: "Any",
    browserRequirements: "Requires JavaScript",
    isPartOf: { "@id": WEBSITE_ID },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
}

// The trail the page draws, crumb for crumb: a graph naming a trail the page does not show is a claim about the page.
function breadcrumbData(path: PagePath): Record<string, unknown> {
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs(path).map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.label,
      item: canonicalUrl(crumb.path),
    })),
  };
}

// The questions as the page shows them, markup taken off: marking up an answer the page does not show word for word is
// what the guidelines forbid, so the text is the rendered paragraph's own.
function faqData(path: UtilityPath, faq: Question[]): Record<string, unknown> {
  return {
    "@type": "FAQPage",
    url: canonicalUrl(path),
    mainEntity: faq.map(({ question, answer }) => ({
      "@type": "Question",
      name: plainText(question),
      acceptedAnswer: { "@type": "Answer", text: plainText(answer) },
    })),
  };
}

export interface StructuredData {
  "@context": string;
  "@graph": Record<string, unknown>[];
}

// Inside a script element nothing is an entity, so the characters that could end it or be taken for markup by whatever
// scans the document are written as JSON's own escapes, which every parser reads back as the characters themselves.
function jsonLdHtml(data: StructuredData): string {
  const json = JSON.stringify(data).replace(
    /[<>&]/g,
    (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`,
  );
  return `<script type="application/ld+json">${json}</script>`;
}

export function headHtml(path: string, content?: PageContent): string {
  const { title, canonical, metas } = headMeta(path);
  const data = structuredData(path, content);

  return [
    `<title>${escapeHtml(title)}</title>`,
    ...metas.map(({ attribute, key, content }) => `<meta ${attribute}="${key}" content="${escapeHtml(content)}" />`),
    `<link rel="canonical" href="${escapeHtml(canonical)}" />`,
    ...(data ? [jsonLdHtml(data)] : []),
  ].join("\n  ");
}

// The head is written into the same document twice: the build fills the empty slot index.html carries with the welcome
// page's, and each copy taken of that document has its own route's head written over it. A write leaves the block
// marked so the next one can find it, which is why this replaces what the last one left instead of adding a second
// head — a page with two titles has told a crawler two different things. The replacement is a function because a
// string would read the `$&` a regex example is entitled to contain as an instruction.
export function withHead(html: string, path: string, content?: PageContent): string {
  return html.replace(HEAD_BLOCK, () => `${HEAD_OPEN}\n  ${headHtml(path, content)}\n  ${HEAD_CLOSE}`);
}

const HEAD_OPEN = "<!--page-head-->";
const HEAD_CLOSE = "<!--/page-head-->";

// The closing marker is optional because the first write finds the bare slot and every later one finds a whole block.
const HEAD_BLOCK = /<!--page-head-->(?:[\s\S]*?<!--\/page-head-->)?/;

// What a page's document names of the page itself: the module the loader imports, marked so it can be found without
// anything in the shell's own bundle naming a page, the chunks a bundler would have preloaded for a dynamic import,
// and the stylesheets a module fetched by address brings none of. All of it is on its way before the shell has run a
// line, which is the half of it the manifest this replaced could never do, having to be fetched and read first.
export interface PageAssets {
  module: string;
  imports: string[];
  css: string[];
}

export type PageAssetsByPath = Partial<Record<PagePath, PageAssets>>;

export function assetsHtml({ module, imports, css }: PageAssets): string {
  const preload = (href: string, page = "") =>
    `<link rel="modulepreload" crossorigin${page} href="${escapeHtml(href)}" />`;

  return [
    ...css.map((href) => `<link rel="stylesheet" crossorigin href="${escapeHtml(href)}" />`),
    preload(module, " data-page"),
    ...imports.map((href) => preload(href)),
  ].join("\n  ");
}

// Written at the end of the head rather than into a slot of index.html's, because the bundler appends the shell's own
// stylesheet and preloads there and a page's must come after them; marked and rewritten the way the head is, so the
// copy taken of a finished document replaces one page's chunks with the next page's rather than naming both.
export function withAssets(html: string, assets: PageAssets | undefined): string {
  if (!assets) return html;
  const block = `${ASSETS_OPEN}\n  ${assetsHtml(assets)}\n  ${ASSETS_CLOSE}\n`;
  return ASSETS_BLOCK.test(html)
    ? html.replace(ASSETS_BLOCK, () => block)
    : html.replace("</head>", () => `${block}</head>`);
}

const ASSETS_OPEN = "<!--page-assets-->";
const ASSETS_CLOSE = "<!--/page-assets-->";

const ASSETS_BLOCK = /<!--page-assets-->[\s\S]*?<!--\/page-assets-->\n?/;

// The other half of what a document is served with, written into the container the application mounts into: the page
// as a reader with no script is handed it, and as the crawler that runs none reads it — the trail, the headline, the
// article, and a link to every utility, since a site whose only links are drawn by its script is a site of orphans to
// anything that does not run it. React clears the container on its first commit, and draws the same words back.
export function bodyHtml(path: string, content?: PageContent): string {
  const meta = pageMeta(path);
  const known = isPagePath(path);
  const home = path === HOME_PATH;
  const article = isUtilityPath(path) && content;

  return [
    "<div class=\"page-fallback\">",
    ...(known && !home ? [crumbsHtml(path as PagePath)] : []),
    "<main>",
    `<h1>${escapeHtml(meta.title)}</h1>`,
    ...(article
      ? [NOSCRIPT, "<article class=\"page-article\">", articleHtml(path, content), "</article>"]
      : [`<p class="page-lede">${escapeHtml(meta.description)}</p>`]),
    ...(home ? [directoryHtml()] : []),
    "</main>",
    footerHtml(!home),
    "</div>",
  ].join("\n");
}

const NOSCRIPT =
  "<noscript><p>The tool on this page runs in your browser and needs JavaScript to do it; the explanation "
  + "below does not.</p></noscript>";

function crumbsHtml(path: PagePath): string {
  const trail = crumbs(path).map((crumb, index, all) =>
    index === all.length - 1
      ? `<li><span aria-current="page">${escapeHtml(crumb.label)}</span></li>`
      : `<li><a href="${crumb.path}">${escapeHtml(crumb.label)}</a></li>`
  );
  return ["<nav class=\"page-crumbs\" aria-label=\"Breadcrumb\">", "<ol>", ...trail, "</ol>", "</nav>"].join("\n");
}

// The welcome page's body is the list of every utility, each named and said in a line, under the group it belongs to.
function directoryHtml(): string {
  return categoriesHtml("page-directory", (path) => {
    const meta = pageMeta(path);
    return `<a href="${path}"><strong>${escapeHtml(meta.label)}</strong> <span>${
      escapeHtml(meta.description)
    }</span></a>`;
  });
}

// Every page but the welcome one ends on a link to every utility; the welcome page's body is that list already.
function footerHtml(directory: boolean): string {
  return [
    "<footer class=\"page-footer\">",
    ...(directory
      ? [categoriesHtml("page-footer-directory", (path) => `<a href="${path}">${escapeHtml(pageMeta(path).label)}</a>`)]
      : []),
    "<p>",
    "© <a href=\"https://manualpilot.com\" rel=\"noopener noreferrer\">Manualpilot</a> ·",
    `<a href="${ATTRIBUTIONS_PATH}">Attributions</a> ·`,
    "<a href=\"https://github.com/manualpilot/utils.plus\" rel=\"noopener noreferrer\">manualpilot/utils.plus</a>",
    "</p>",
    "</footer>",
  ].join("\n");
}

function categoriesHtml(className: string, item: (path: UtilityPath) => string): string {
  const groups = CATEGORIES.map(({ name, paths }) =>
    ["<section>", `<h2>${escapeHtml(name)}</h2>`, listHtml(paths.map(item)), "</section>"].join("\n")
  );
  return [`<nav class="${className}" aria-label="Utilities">`, ...groups, "</nav>"].join("\n");
}

// Marked and rewritten the way the head is, so the copy taken of a finished document replaces a body rather than
// nesting a second one inside the first.
export function withBody(html: string, path: string, content?: PageContent): string {
  return html.replace(BODY_BLOCK, () => `${BODY_OPEN}${bodyHtml(path, content)}${BODY_CLOSE}`);
}

const BODY_OPEN = "<!--page-body-->";
const BODY_CLOSE = "<!--/page-body-->";

const BODY_BLOCK = /<!--page-body-->(?:[\s\S]*?<!--\/page-body-->)?/;

// Every document the build emits beside index.html, taken from it: one per address the site answers for, and none for
// the addresses it does not. There is no server to write a head per request, so each is written once here instead.
export function pageDocuments(
  index: string,
  contents: PageContents,
  assets: PageAssetsByPath = {},
): Record<string, string> {
  const documents: Record<string, string> = {};

  for (const path of pagePaths().filter((page) => page !== HOME_PATH)) {
    const content = isUtilityPath(path) ? contents[path] : undefined;
    const head = withAssets(withHead(index, path, content), assets[path]);
    documents[documentFileName(path)] = withBody(head, path, content);
  }

  return documents;
}
